import { Link } from "react-router-dom";
import { Star, Languages, Clock, BookOpenCheck, BadgeCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const subjectColor = {
  French: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-900",
  English: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-900",
  Spanish: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-900",
  Math: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-900",
};

function KindBadge({ kind }) {
  if (kind === "guide") {
    return (
      <Badge
        variant="outline"
        className="rounded-full font-medium text-[11px] bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-900 inline-flex items-center gap-1"
        data-testid="profile-kind-badge"
      >
        <BookOpenCheck className="h-3 w-3" /> Guide
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="rounded-full font-medium text-[11px] bg-primary/10 text-primary border-primary/30 inline-flex items-center gap-1"
      data-testid="profile-kind-badge"
    >
      <BadgeCheck className="h-3 w-3" /> Verified Tutor
    </Badge>
  );
}

export default function TutorCard({ tutor, onBook }) {
  const initial = tutor.name?.slice(0, 1) || "T";
  const isGuide = tutor.kind === "guide";

  return (
    <Card
      className="group p-5 sm:p-6 flex flex-col gap-4 border-border/70 hover:border-primary/40 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 bg-card"
      data-testid="tutor-card"
    >
      <div className="flex items-start gap-4">
        <div className="relative">
          <Avatar className="h-16 w-16 ring-2 ring-primary/20">
            <AvatarImage src={tutor.photo} alt={tutor.name} />
            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
              {initial}
            </AvatarFallback>
          </Avatar>
          <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-emerald-500 ring-2 ring-card" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading text-lg font-semibold truncate">{tutor.name}</h3>
            <div className="flex items-center gap-1 text-sm font-medium text-amber-600 dark:text-amber-400 shrink-0">
              <Star className="h-4 w-4 fill-current" />
              {tutor.rating?.toFixed?.(2) ?? tutor.rating}
              <span className="text-muted-foreground font-normal">({tutor.reviews})</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{tutor.headline}</p>
          <div className="mt-1.5">
            <KindBadge kind={tutor.kind} />
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground line-clamp-2">{tutor.bio}</p>

      <div className="flex flex-wrap gap-1.5">
        {tutor.subjects?.map((s) => (
          <Badge key={s} variant="outline" className={`rounded-full font-medium ${subjectColor[s] || ""}`}>
            {s}
          </Badge>
        ))}
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Languages className="h-3.5 w-3.5" />
          {tutor.languages?.slice(0, 2).join(", ") || "English"}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {tutor.level}
        </span>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border/60 gap-3 flex-wrap">
        <div>
          <span className="font-heading text-2xl font-bold">${tutor.hourly_rate}</span>
          <span className="text-sm text-muted-foreground">/hr</span>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="rounded-full flex-1 sm:flex-none"
            data-testid="tutor-card-view-profile-button"
          >
            <Link to={`/tutors/${tutor.id}`}>View profile</Link>
          </Button>
          <Button
            size="sm"
            onClick={() => onBook?.(tutor)}
            className="rounded-full flex-1 sm:flex-none"
            data-testid="tutor-card-book-session-button"
          >
            {isGuide ? "Book guide" : "Book session"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
